import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { IdbStorage } from "@/store/idbStorage";
import {
  PendingSaleScope,
  PendingSaleSnapshot,
} from "@/pages/point-of-sale/models/pending-sale.model";

export interface PendingSalesState {
  pendingSales: PendingSaleSnapshot[];
  /** ID de la venta pendiente cargada en el carrito actual (se elimina al cobrar). */
  loadedPendingSaleId: string | null;
  addPendingSale: (snapshot: PendingSaleSnapshot) => void;
  updatePendingSale: (snapshot: PendingSaleSnapshot) => void;
  deletePendingSale: (id: string) => void;
  setLoadedPendingSaleId: (id: string | null) => void;
  completeLoadedPendingSale: () => void;
  getPendingSalesForScope: (scope: PendingSaleScope) => PendingSaleSnapshot[];
  clearPendingSales: () => void;
  resetStore: () => void;
}

export const usePendingSalesStore = create<PendingSalesState>()(
  devtools(
    persist(
      (set, get) => ({
        pendingSales: [],
        loadedPendingSaleId: null,

        addPendingSale: (snapshot) =>
          set((state) => ({
            pendingSales: [snapshot, ...state.pendingSales],
          })),

        updatePendingSale: (snapshot) =>
          set((state) => ({
            pendingSales: state.pendingSales.map((entry) =>
              entry.id === snapshot.id ? snapshot : entry
            ),
          })),

        deletePendingSale: (id) =>
          set((state) => ({
            pendingSales: state.pendingSales.filter((entry) => entry.id !== id),
            loadedPendingSaleId:
              state.loadedPendingSaleId === id
                ? null
                : state.loadedPendingSaleId,
          })),

        setLoadedPendingSaleId: (id) => set({ loadedPendingSaleId: id }),

        /** Elimina la venta pendiente activa tras cobrar con éxito. */
        completeLoadedPendingSale: () => {
          const id = get().loadedPendingSaleId;
          if (!id) return;
          set((state) => ({
            pendingSales: state.pendingSales.filter((entry) => entry.id !== id),
            loadedPendingSaleId: null,
          }));
        },

        getPendingSalesForScope: (scope) =>
          get()
            .pendingSales.filter(
              (entry) =>
                entry.storeId === scope.storeId &&
                entry.cashierId === scope.cashierId &&
                entry.sellerId === scope.sellerId
            )
            .sort((a, b) => b.updatedAt - a.updatedAt),

        clearPendingSales: () =>
          set({ pendingSales: [], loadedPendingSaleId: null }),

        resetStore: () => set({ pendingSales: [], loadedPendingSaleId: null }),
      }),
      {
        name: "pending-sales-store",
        storage: createJSONStorage(() => IdbStorage),
      }
    )
  )
);
